import { Factory, Model } from 'miragejs'
import type { MockServer } from '@/api/mock-server/server.js'
import {
  NORDHEALTH_PAY_ENDPOINTS,
  NORDHEALTH_PAY_RESOURCE_TYPES,
} from '@/constants/reports/nordhealthPayReport.js'
import { filterable, pageable, sortable } from '../utils.js'
import type { NordhealthPayListItemAttributes } from '@/models/reports/nordhealthPayReport.js'
import {
  rand,
  randAmount,
  randBetweenDate,
  randBoolean,
  randFullName,
  randNumber,
} from '@ngneat/falso'
import { formatToJSDate } from '@/utils/date/conversion.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { isAfter, isBefore } from '@/utils/date/validation.js'

export type NordhealthPayPaymentData = NordhealthPayListItemAttributes

const MOCK_KEY = 'nordhealthPayReport'

export const model = Model.extend({})

export const factory = Factory.extend<NordhealthPayPaymentData>({
  payment_type() {
    return rand([
      'Payment',
      'Reversal',
      'Refund',
      'Dispute',
      'Chargeback',
      'ChargebackReversed',
    ])
  },
  status() {
    return rand(['Success', 'Fail'])
  },
  payment_method() {
    return rand([
      'visa',
      'mastercard',
      'apple_pay',
      'amex',
      'paypal',
      'klarna',
      'google_pay',
      'maestro',
    ])
  },
  account_description() {
    return rand(['Main Account', 'Secondary Account'])
  },
  payment_amount() {
    const isRefund =
      this.payment_type === 'Refund' || this.payment_type === 'Dispute'
    return isRefund ? 0 : +randAmount({ max: 500, min: 10 })
  },
  refund_amount() {
    const isRefund =
      this.payment_type === 'Refund' || this.payment_type === 'Dispute'
    return isRefund ? +randAmount({ max: 500, min: 10 }) : 0
  },
  card_last_four() {
    return randNumber({ max: 9999, min: 1000 }).toString()
  },
  client() {
    return randFullName()
  },
  transaction_id() {
    return `TXN-${randNumber({ max: 999999999, min: 100000000 })}`
  },
  currency() {
    return rand(['EUR', 'USD', 'GBP', 'SEK', 'NOK', 'DKK'])
  },
  nordhealth_fee() {
    return +randAmount({ max: 4, min: 0.2 })
  },
  other_fees() {
    return +randAmount({ max: 2, min: 0.1 })
  },
  total_fee: 0, // Will be computed in afterCreate
  net_amount: 0, // Will be computed in afterCreate
  paid_out() {
    // Can only be paid out if status is Success
    if (this.status !== 'Success') {
      return 'No'
    }
    return randBoolean() ? 'Yes' : 'No'
  },
  payment_date() {
    return randBetweenDate({
      from: formatToJSDate('2023-01-01'),
      to: formatToJSDate(getTodayDateTimeISO()),
    }).toISOString()
  },
  payout_date() {
    // Can only have payout date if paid_out is 'Yes'
    if (this.paid_out !== 'Yes') {
      return null
    }
    return randBetweenDate({
      from: formatToJSDate('2023-01-01'),
      to: formatToJSDate(getTodayDateTimeISO()),
    })
      .toISOString()
      .split('T')[0]
  },
  psp_reference() {
    return randNumber({
      max: 9999999999,
      min: 1000000000,
    }).toString()
  },
  related_invoice() {
    return `INV-${randNumber({ max: 99999, min: 10000 })}`
  },
  terminal() {
    return `TERM-${randNumber({ max: 9999999999, min: 1000000000 })}`
  },
})

export function seeds(server: MockServer) {
  server.createList(MOCK_KEY, 200)
}

/**
 * Helper to check if an item's value is included in a comma-separated filter string
 */
const includesInCommaSeparated = (itemValue: string, filterValue: string) =>
  filterValue.split(',').includes(itemValue)

export function routes(server: MockServer) {
  server.get(NORDHEALTH_PAY_ENDPOINTS.LIST, function (schema, request) {
    const results = schema.all(MOCK_KEY)

    const sorted = sortable(results, request, {
      fees: (item) => item.attrs.total_fee,
      net: (item) => item.attrs.net_amount,
    })

    const filtered = filterable(sorted, request, {
      account: (item, value) => item.attrs.account_description.includes(value),
      end_date: (item, value) => !isAfter(item.attrs.payment_date, value),
      paid_out: (item, value) =>
        includesInCommaSeparated(item.attrs.paid_out, value),
      payment_method: (item, value) =>
        includesInCommaSeparated(item.attrs.payment_method, value),
      payment_type: (item, value) =>
        includesInCommaSeparated(item.attrs.payment_type, value),
      search: (item, value) => {
        const searchLower = value.toLowerCase()
        return (
          item.attrs.psp_reference.toLowerCase().includes(searchLower) ||
          item.attrs.related_invoice.toLowerCase().includes(searchLower)
        )
      },
      start_date: (item, value) => !isBefore(item.attrs.payment_date, value),
      status: (item, value) =>
        includesInCommaSeparated(item.attrs.status, value),
    })

    const { data, meta } = pageable(filtered, request)

    return {
      data: data.models.map((item) => {
        const total_fee =
          (item.attrs.nordhealth_fee || 0) + (item.attrs.other_fees || 0)
        const net_amount = (item.attrs.payment_amount || 0) - total_fee
        return {
          id: item.id,
          type: NORDHEALTH_PAY_RESOURCE_TYPES.PAYMENT,
          attributes: {
            ...item.attrs,
            net_amount,
            total_fee,
          },
        }
      }),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get(NORDHEALTH_PAY_ENDPOINTS.AGGREGATIONS, function (schema, request) {
    const results = schema.all(MOCK_KEY)

    // Use same filter handlers as list endpoint
    const filtered = filterable(results, request, {
      account: (item, value) => item.attrs.account_description.includes(value),
      end_date: (item, value) => !isAfter(item.attrs.payment_date, value),
      paid_out: (item, value) =>
        includesInCommaSeparated(item.attrs.paid_out, value),
      payment_method: (item, value) =>
        includesInCommaSeparated(item.attrs.payment_method, value),
      payment_type: (item, value) =>
        includesInCommaSeparated(item.attrs.payment_type, value),
      search: (item, value) => {
        const searchLower = value.toLowerCase()
        return (
          item.attrs.psp_reference.toLowerCase().includes(searchLower) ||
          item.attrs.related_invoice.toLowerCase().includes(searchLower)
        )
      },
      start_date: (item, value) => !isBefore(item.attrs.payment_date, value),
      status: (item, value) =>
        includesInCommaSeparated(item.attrs.status, value),
    })

    const aggregations = filtered.models.reduce(
      (acc, curr) => {
        acc.payment_count++
        if (curr.attrs.payment_type === 'Dispute') {
          acc.dispute_count++
        }
        if (curr.attrs.payment_type === 'Refund') {
          acc.refund_count++
        }
        return acc
      },
      {
        dispute_count: 0,
        payment_count: 0,
        refund_count: 0,
      },
    )

    return {
      data: {
        id: 'aggregations',
        type: NORDHEALTH_PAY_RESOURCE_TYPES.AGGREGATION,
        attributes: aggregations,
      },
    }
  })

  server.get(NORDHEALTH_PAY_ENDPOINTS.FILTERS, (schema, _request) => {
    const allPayments = schema.all('nordhealthPayReport').models

    const uniqueValues = (key: keyof NordhealthPayPaymentData) =>
      Array.from(new Set(allPayments.map((p) => p.attrs[key])))

    const filters = {
      account: uniqueValues('account_description'),
      payment_method: uniqueValues('payment_method'),
      payment_type: uniqueValues('payment_type'),
      status: uniqueValues('status'),
    }

    return {
      data: {
        id: 'filters',
        type: NORDHEALTH_PAY_RESOURCE_TYPES.FILTERS,
        attributes: filters,
      },
    }
  })
}
